import { HttpInterceptorFn, HttpErrorResponse } from "@angular/common/http";
import { catchError, throwError } from "rxjs";
import type { AppError } from "../types/common.types";

/**
 * HTTP error interceptor for handling API errors
 * Converts HTTP errors to AppError format
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) =>
  next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const appError: AppError = {
        type: "UnknownError",
        message: "An unexpected error occurred",
        statusCode: error.status,
      };

      switch (error.status) {
        case 400: {
          appError.type = "ValidationError";
          appError.message =
            (
              error.error as {
                message?: string;
                detail?: string;
                errors?: unknown;
              }
            )?.message ??
            (error.error as { detail?: string })?.detail ??
            "Invalid request";
          const errorObj400 = error.error as { errors?: unknown };
          appError.details =
            errorObj400?.errors !== undefined &&
            typeof errorObj400.errors === "object" &&
            !Array.isArray(errorObj400.errors)
              ? (errorObj400.errors as Record<string, unknown>)
              : undefined;
          break;
        }

        case 401:
          appError.type = "AuthenticationError";
          appError.message =
            (error.error as { message?: string; detail?: string })?.message ??
            (error.error as { detail?: string })?.detail ??
            "Authentication required";
          break;

        case 403:
          appError.type = "AuthorizationError";
          appError.message =
            "You do not have permission to perform this action";
          break;

        case 423:
          appError.type = "AccountLockedError";
          appError.message =
            (error.error as { message?: string; detail?: string })?.message ??
            (error.error as { detail?: string })?.detail ??
            "Account is locked due to multiple failed login attempts";
          break;

        case 404:
          appError.type = "NotFoundError";
          appError.message =
            (error.error as { message?: string; detail?: string })?.message ??
            (error.error as { detail?: string })?.detail ??
            "Resource not found";
          break;

        case 422: {
          appError.type = "ValidationError";
          appError.message =
            (error.error as { message?: string; detail?: string })?.message ??
            (error.error as { detail?: string })?.detail ??
            "Validation failed";
          const errorObj422 = error.error as { errors?: unknown };
          appError.details =
            errorObj422?.errors !== undefined &&
            typeof errorObj422.errors === "object" &&
            !Array.isArray(errorObj422.errors)
              ? (errorObj422.errors as Record<string, unknown>)
              : undefined;
          break;
        }

        case 500:
        case 502:
          appError.type = "ServerError";
          appError.message = "Server error. Please try again later.";
          break;

        case 503:
          appError.type = "ServiceUnavailableError";
          appError.message =
            (error.error as { message?: string; detail?: string })?.message ??
            (error.error as { detail?: string })?.detail ??
            "Unable to authenticate at this time. Please try again later.";
          break;

        case 0:
          appError.type = "NetworkError";
          appError.message = "Network error. Please check your connection.";
          break;

        default:
          appError.message =
            (error.error as { message?: string; detail?: string })?.message ??
            (error.error as { detail?: string })?.detail ??
            appError.message;
      }

      console.error("HTTP Error:", appError);
      return throwError(() => appError);
    }),
  );
